import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

interface Document {
  id: string;
  title: string;
  status: string;
  user_id: string;
  file_size: number;
  created_at: string;
}

interface VerificationPanelProps {
  refreshTrigger: number;
}

export default function VerificationPanel({ refreshTrigger }: VerificationPanelProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifierId] = useState('verifier-' + Math.random().toString(36).substr(2, 9));
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const [comments, setComments] = useState('');

  useEffect(() => {
    loadPendingDocuments();
  }, [refreshTrigger]);

  const loadPendingDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/documents?status=pending_verification`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao carregar documentos');
      }

      setDocuments(data.data.documents || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (docId: string, status: 'approved' | 'rejected') => {
    try {
      const response = await fetch(`${API_URL}/api/documents/${docId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          comments: comments || undefined,
          verifierId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao verificar');
      }

      setComments('');
      setSelectedDoc(null);
      loadPendingDocuments();
    } catch (err: any) {
      alert('Erro: ' + err.message);
    }
  };

  if (loading) return <div className="loading">Carregando documentos pendentes...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!documents.length) return <div className="empty-state">Nenhum documento aguardando verificação</div>;

  return (
    <div className="verification-panel">
      <div className="documents-grid">
        {documents.map((doc) => (
          <div key={doc.id} className="document-card">
            <h3>{doc.title}</h3>
            <p>ID: {doc.id}</p>
            <p>Enviado em: {new Date(doc.created_at).toLocaleDateString('pt-BR')}</p>
            <button
              className="btn btn-small"
              onClick={() => setSelectedDoc(doc.id)}
            >
              Verificar
            </button>
          </div>
        ))}
      </div>

      {selectedDoc && (
        <div className="modal-overlay" onClick={() => setSelectedDoc(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Verificar Documento</h2>
            <textarea
              placeholder="Adicionar comentários sobre a verificação..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
            />
            <div className="modal-actions">
              <button
                className="btn btn-danger"
                onClick={() => handleVerification(selectedDoc, 'rejected')}
              >
                ❌ Rejeitar
              </button>
              <button
                className="btn btn-success"
                onClick={() => handleVerification(selectedDoc, 'approved')}
              >
                ✅ Aprovar
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedDoc(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
